import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, InputNumber, Button, Space, Typography, message, Divider, Row, Col, TimePicker } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { scheduleApi, BookingSettings as BookingSettingsType } from '../../services/scheduleApi';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const BookingSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customBookingHours, setCustomBookingHours] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const settings = await scheduleApi.getBookingSettings();
      form.setFieldsValue({
        ...settings,
        booking_start_time: settings.booking_start_time ? dayjs(settings.booking_start_time, 'HH:mm') : null,
        booking_end_time: settings.booking_end_time ? dayjs(settings.booking_end_time, 'HH:mm') : null
      });
      setCustomBookingHours(settings.custom_booking_hours);
    } catch (error) {
      console.error('Error loading booking settings:', error);
      message.error('Failed to load booking settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const settings: Partial<BookingSettingsType> = {
        ...values,
        booking_start_time: values.booking_start_time?.format('HH:mm'),
        booking_end_time: values.booking_end_time?.format('HH:mm')
      };
      await scheduleApi.updateBookingSettings(settings);
      message.success('Booking settings saved successfully');
    } catch (error) {
      console.error('Error saving booking settings:', error);
      message.error('Failed to save booking settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Customer Booking Settings</Title>
      <Text type="secondary">Configure how customers can book appointments</Text>

      <Card style={{ marginTop: '24px' }} loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            allow_customer_booking: true,
            max_daily_appointments: 10,
            buffer_time_minutes: 15,
            booking_advance_days_min: 1,
            booking_advance_days_max: 30,
            allow_diagnosis_booking: true,
            allow_repair_booking: false,
            allow_inspection_booking: true,
            default_diagnosis_duration_minutes: 60,
            default_repair_duration_minutes: 120,
            default_inspection_duration_minutes: 30,
            auto_assign_technician: true,
            prefer_nearest_technician: true,
            balance_workload: true,
            send_confirmation_email: true,
            send_confirmation_sms: false,
            send_reminder_email: true,
            send_reminder_sms: false,
            reminder_hours_before: 24,
            custom_booking_hours: false
          }}
        >
          {/* General Settings */}
          <Title level={4}>General Settings</Title>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Allow Customer Booking"
                name="allow_customer_booking"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Max Daily Appointments"
                name="max_daily_appointments"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={1} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Buffer Time (minutes)"
                name="buffer_time_minutes"
                rules={[{ required: true, message: 'Required' }]}
                tooltip="Time between appointments"
              >
                <InputNumber min={0} max={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Booking Window */}
          <Title level={4}>Booking Window</Title>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Minimum Days in Advance"
                name="booking_advance_days_min"
                rules={[{ required: true, message: 'Required' }]}
                tooltip="How many days in advance customers must book"
              >
                <InputNumber min={0} max={30} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Maximum Days in Advance"
                name="booking_advance_days_max"
                rules={[{ required: true, message: 'Required' }]}
                tooltip="How far in advance customers can book"
              >
                <InputNumber min={1} max={365} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Appointment Types */}
          <Title level={4}>Allowed Appointment Types</Title>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Allow Diagnosis Booking"
                name="allow_diagnosis_booking"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Allow Repair Booking"
                name="allow_repair_booking"
                valuePropName="checked"
                tooltip="Usually requires estimate approval first"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Allow Inspection Booking"
                name="allow_inspection_booking"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Default Durations */}
          <Title level={4}>Default Appointment Durations</Title>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Diagnosis (minutes)"
                name="default_diagnosis_duration_minutes"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={15} max={480} step={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Repair (minutes)"
                name="default_repair_duration_minutes"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={15} max={480} step={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Inspection (minutes)"
                name="default_inspection_duration_minutes"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={15} max={480} step={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Auto-Assignment */}
          <Title level={4}>Technician Assignment</Title>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Auto-Assign Technician"
                name="auto_assign_technician"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Prefer Nearest Technician"
                name="prefer_nearest_technician"
                valuePropName="checked"
                tooltip="Assign based on location if available"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Balance Workload"
                name="balance_workload"
                valuePropName="checked"
                tooltip="Distribute appointments evenly"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Notifications */}
          <Title level={4}>Customer Notifications</Title>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Send Confirmation Email"
                name="send_confirmation_email"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Send Confirmation SMS"
                name="send_confirmation_sms"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Send Reminder Email"
                name="send_reminder_email"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Send Reminder SMS"
                name="send_reminder_sms"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Reminder Hours Before"
                name="reminder_hours_before"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={1} max={168} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Custom Booking Hours */}
          <Title level={4}>Custom Booking Hours</Title>
          <Form.Item
            label="Use Custom Booking Hours"
            name="custom_booking_hours"
            valuePropName="checked"
            tooltip="Override regular technician hours for customer booking"
          >
            <Switch onChange={setCustomBookingHours} />
          </Form.Item>

          {customBookingHours && (
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="Booking Start Time"
                  name="booking_start_time"
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Booking End Time"
                  name="booking_end_time"
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Divider />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              Save Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
